import React, { useState } from 'react';
import {
  IAuditoriaAdiantamentoClientesResultado,
  IItemAdiantamentoClientes,
} from '../interfaces/IAuditoriaAdiantamentoClientes';
import { formatCurrency, formatDate } from '../utils/formatters';

interface Props {
  resultado: IAuditoriaAdiantamentoClientesResultado;
}



const formatPercent = (value?: number) =>
  value !== undefined ? `${(value * 100).toFixed(2).replace('.', ',')}%` : '-';

const getIconeStatus = (status: string) => {
  switch (status) {
    case 'ERRO':
      return 'bi-exclamation-circle-fill';
    case 'ALERTA':
      return 'bi-exclamation-triangle-fill';
    case 'AVISO':
      return 'bi-info-circle-fill';
    default:
      return 'bi-check-circle-fill';
  }
};

// COR-278: drill-down a nível de lançamento — todo ERRO precisa mostrar não só o saldo agregado,
// mas os lançamentos reais (créditos de entrada + débitos de baixa/NF) que o formam.
const LIMITE_LANCAMENTOS_VISIVEIS = 10;

const BlocoLancamentosRelacionados: React.FC<{ item: IItemAdiantamentoClientes }> = ({ item }) => {
  const [mostrarTodos, setMostrarTodos] = useState(false);
  const lancamentos = item.lancamentosRelacionados || [];

  if (lancamentos.length === 0) return null;

  const visiveis = mostrarTodos ? lancamentos : lancamentos.slice(0, LIMITE_LANCAMENTOS_VISIVEIS);

  return (
    <div className='mt-2'>
      <strong>Lançamentos Relacionados ({lancamentos.length}):</strong>
      <div className='table-responsive mt-1'>
        <table className='auditoria-table' style={{ fontSize: 12 }}>
          <thead>
            <tr>
              <th>Data</th>
              <th>Nº Lançamento</th>
              <th>Histórico</th>
              <th className='text-end'>Débito</th>
              <th className='text-end'>Crédito</th>
            </tr>
          </thead>
          <tbody>
            {visiveis.map((lanc, idx) => (
              <tr key={`${lanc.numeroLancamento}-${lanc.data}-${idx}`}>
                <td>{formatDate(lanc.data)}</td>
                <td><code>{lanc.numeroLancamento || '-'}</code></td>
                <td>{lanc.historico}</td>
                <td className='text-end'>{lanc.valorDebito > 0 ? formatCurrency(lanc.valorDebito) : '-'}</td>
                <td className='text-end'>{lanc.valorCredito > 0 ? formatCurrency(lanc.valorCredito) : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {lancamentos.length > LIMITE_LANCAMENTOS_VISIVEIS && !mostrarTodos && (
        <button
          type='button'
          className='btn btn-link btn-sm p-0 mt-1'
          style={{ fontSize: 12 }}
          onClick={() => setMostrarTodos(true)}
        >
          + {lancamentos.length - LIMITE_LANCAMENTOS_VISIVEIS} lançamento(s) adicional(is)
        </button>
      )}
    </div>
  );
};

const AuditoriaAdiantamentoClientesDetalhe: React.FC<Props> = ({ resultado }) => {
  const [itemExpandido, setItemExpandido] = useState<string | null>(null);
  const [fundamentacaoAberta, setFundamentacaoAberta] = useState(false);

  const adiantamentosEstagnados = resultado.adiantamentosEstagnados || [];
  const adiantamentosConciliados = resultado.adiantamentosConciliados || [];

  const alternarExpandir = (chave: string) => {
    setItemExpandido(itemExpandido === chave ? null : chave);
  };

  return (
    <>
      <div className={`auditoria-alert status-${resultado.status}`}>
        <i className={`bi ${getIconeStatus(resultado.status)} auditoria-alert-icon`} />
        <div>
          <strong>Auditoria de Adiantamentos de Clientes (Passivo) — {resultado.status}</strong>
          <div>{resultado.mensagemDirecionamento}</div>
        </div>
      </div>

      <div className='auditoria-kpis'>
        <div className={`auditoria-kpi ${resultado.impactoFiscalTotal > 0 ? 'auditoria-kpi-critico' : 'auditoria-kpi-disabled'}`}>
          <div className='auditoria-kpi-label'>Impacto Fiscal Estimado</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.impactoFiscalTotal)}</div>
          {resultado.impactoFiscalTotal === 0 ? (
            <span className='auditoria-kpi-selo'>Zero Desperdício</span>
          ) : (
            <div className='auditoria-kpi-help'>Tributos por regime + Multa ECD potencial (5% limitada a 1% da receita bruta do período — Art. 12, II, Lei 8.218/1991).</div>
          )}
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Erros / Alertas / Avisos</div>
          <div className='auditoria-kpi-value'>
            <span className='auditoria-contador auditoria-contador-erro'>{resultado.totalErros}</span>{' '}
            <span className='auditoria-contador auditoria-contador-alerta'>{resultado.totalAlertas}</span>{' '}
            <span className='auditoria-contador auditoria-contador-aviso'>{resultado.totalAvisos}</span>
          </div>
          <div className='auditoria-kpi-help'>Adiantamentos de clientes sem NF emitida por mais de 30 dias.</div>
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Fundamentação</div>
          <div className='auditoria-kpi-value'>
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={() => setFundamentacaoAberta(!fundamentacaoAberta)}
            >
              <i className='bi bi-journal-check me-1' />
              {fundamentacaoAberta ? 'Ocultar fundamentação' : 'Ver fundamentação legal'}
            </button>
          </div>
          <div className='auditoria-kpi-help'>Base legal aplicada à auditoria de passivo.</div>
        </div>
      </div>

      {fundamentacaoAberta && (
        <div className='auditoria-card'>
          <div><strong>Estagnação de Adiantamentos de Clientes:</strong> {resultado.fundamentacaoEstagnacao}</div>
        </div>
      )}

      {/* Adiantamentos Estagnados (> 30 dias sem NF emitida) */}
      <div className='auditoria-card'>
        <h3>Adiantamentos de Clientes Estagnados (&gt; 30 dias sem NF emitida)</h3>
        <p className='text-muted'>
          Contas de Adiantamento de Clientes (Passivo Circulante) com saldo credor sem baixa (débito = emissão de NF)
          por mais de 30 dias — presunção de receita disponível (CPC 47 / IFRS 15 + Art. 27 da Lei 10.833/2003).
        </p>

        {adiantamentosEstagnados.length === 0 && (
          <div className='text-muted mt-4'>Nenhum adiantamento de cliente estagnado identificado no período.</div>
        )}

        {adiantamentosEstagnados.length > 0 && (
          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th>Último Crédito</th>
                  <th className='text-center'>Dias Estagnado</th>
                  <th>Regime</th>
                  <th className='text-end'>Saldo Estagnado</th>
                  <th className='text-end'>Multa Total</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {adiantamentosEstagnados.map((item: IItemAdiantamentoClientes, idx) => {
                  const chave = `ae-${item.empresaId}-${item.contaContabil}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <strong>{item.descricaoConta || item.contaContabil}</strong>
                          {item.contaReferencial && <div className='text-muted' style={{ fontSize: 11 }}>{item.contaReferencial}</div>}
                        </td>
                        <td>{formatDate(item.dataUltimoCredito)}</td>
                        <td className='text-center'>
                          <span className={`auditoria-status status-${item.status}`}>{item.diasEstagnado} dias</span>
                        </td>
                        <td>
                          <span style={{ fontSize: 12 }}>{item.regimeTributario || '-'}</span>
                        </td>
                        <td className='text-end'>{formatCurrency(item.saldoEstagnado)}</td>
                        <td className='text-end'>{formatCurrency(item.multaTotal)}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={7} style={{ background: '#fafbfc' }}>
                            <div className='py-2 px-3'>
                              <div><strong>Diagnóstico:</strong> {item.mensagemDiagnostico}</div>
                              <div><strong>Memória de Cálculo:</strong> {item.memoriaCalculo}</div>
                              <div className='mt-1 text-muted' style={{ fontSize: 12 }}>
                                Alíquota tributos ({item.regimeTributario}): {formatPercent(item.aliquotaTributos)} → {formatCurrency(item.multaTributos)} |
                                Multa ECD Potencial: {formatCurrency(item.multaEcd)}
                              </div>
                              <BlocoLancamentosRelacionados item={item} />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Adiantamentos Conciliados — visibilidade de que a auditoria rodou */}
      {adiantamentosConciliados.length > 0 && (
        <div className='auditoria-card'>
          <h3>
            <i className='bi bi-check-circle-fill text-success me-2' style={{ fontSize: 18 }} />
            Adiantamentos de Clientes Verificados e Conciliados
          </h3>
          <p className='text-muted'>
            Contas de adiantamento de clientes que foram auditadas e estão em conformidade — saldo zerado ou NF emitida dentro de 30 dias.
          </p>

          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th>Último Crédito</th>
                  <th className='text-center'>Dias</th>
                  <th className='text-end'>Saldo</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {adiantamentosConciliados.map((item: IItemAdiantamentoClientes, idx) => {
                  const chave = `ac-${item.empresaId}-${item.contaContabil}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <strong>{item.descricaoConta || item.contaContabil}</strong>
                          {item.contaReferencial && <div className='text-muted' style={{ fontSize: 11 }}>{item.contaReferencial}</div>}
                        </td>
                        <td>{item.dataUltimoCredito ? formatDate(item.dataUltimoCredito) : '-'}</td>
                        <td className='text-center'>{item.diasEstagnado || '-'}</td>
                        <td className='text-end'>{formatCurrency(item.saldoEstagnado)}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={5} style={{ background: '#f0fdf4' }}>
                            <div className='py-2 px-3'>
                              <i className='bi bi-check-circle-fill text-success me-1' />
                              <strong>Diagnóstico:</strong> {item.mensagemDiagnostico}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
};

export default AuditoriaAdiantamentoClientesDetalhe;
